import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePartners, Partner } from "@/context/PartnerContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  Check,
  Edit,
  MapPin,
  MoreVertical,
  Plus,
  Search,
  Trash,
  X,
  HeartHandshake,
  Map,
  ArrowRight,
  Mail,
  UserPlus,
  Phone,
  Users,
  TrendingUp,
  Copy,
  AlertTriangle,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { ServiceType, useLeads } from "@/context/LeadContext";
import ServiceBadge from "@/components/ui-custom/ServiceBadge";
import { Loader2 } from "lucide-react";

const Partners: React.FC = () => {
  const { partners, removePartner, refreshPartners } = usePartners();
  const { leads } = useLeads();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceFilter, setServiceFilter] = useState<ServiceType | 'all'>('all');
  const [focusFilter, setFocusFilter] = useState<'all' | 'needsAttention' | 'topPerformers'>('all');
  const [sortBy, setSortBy] = useState<'leadsDesc' | 'conversionDesc' | 'nameAsc'>('leadsDesc');
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRegLinkDialog, setShowRegLinkDialog] = useState(false);

  const partnerStatsById = useMemo(() => {
    const stats: Record<string, { leads: number; success: number; conversion: number; needsAttention: boolean }> = {};

    partners.forEach(partner => {
      const partnerLeads = leads.filter(lead => lead.partnerId === partner.id);
      const success = partnerLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
      const conversion = partnerLeads.length > 0 ? Math.round((success / partnerLeads.length) * 100) : 0;

      stats[partner.id] = {
        leads: partnerLeads.length,
        success,
        conversion,
        needsAttention:
          (partnerLeads.length >= 5 && conversion < 20) ||
          (!partner.email && partner.active) ||
          partner.sms_opt_out,
      };
    });

    return stats;
  }, [leads, partners]);

  const filteredPartners = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    let result = partners.filter((partner) => {
      const partnerMatch =
        !normalizedSearch ||
        partner.name.toLowerCase().includes(normalizedSearch) ||
        partner.phone.includes(normalizedSearch) ||
        partner.location.toLowerCase().includes(normalizedSearch) ||
        (partner.email || '').toLowerCase().includes(normalizedSearch);

      const serviceMatch =
        serviceFilter === 'all' ||
        partner.services.includes(serviceFilter);

      const attention = partnerStatsById[partner.id]?.needsAttention ?? false;
      const conversion = partnerStatsById[partner.id]?.conversion ?? 0;
      const leadsCount = partnerStatsById[partner.id]?.leads ?? 0;

      const focusMatch =
        focusFilter === 'all' ||
        (focusFilter === 'needsAttention' && attention) ||
        (focusFilter === 'topPerformers' && conversion >= 40 && leadsCount >= 5);

      return partnerMatch && serviceMatch && focusMatch;
    });

    result = [...result].sort((a, b) => {
      if (sortBy === 'nameAsc') {
        return a.name.localeCompare(b.name, 'el');
      }

      if (sortBy === 'conversionDesc') {
        const aConversion = partnerStatsById[a.id]?.conversion ?? 0;
        const bConversion = partnerStatsById[b.id]?.conversion ?? 0;
        return bConversion - aConversion;
      }

      const aLeads = partnerStatsById[a.id]?.leads ?? 0;
      const bLeads = partnerStatsById[b.id]?.leads ?? 0;
      return bLeads - aLeads;
    });

    return result;
  }, [focusFilter, partnerStatsById, partners, searchTerm, serviceFilter, sortBy]);

  const activePartners = filteredPartners.filter((partner) => partner.active);
  const inactivePartners = filteredPartners.filter((partner) => !partner.active);

  const partnersSummary = useMemo(() => {
    const active = partners.filter(partner => partner.active).length;
    const attention = partners.filter(partner => partnerStatsById[partner.id]?.needsAttention).length;
    const topPerformers = partners.filter(partner => {
      const s = partnerStatsById[partner.id];
      return (s?.conversion ?? 0) >= 40 && (s?.leads ?? 0) >= 5;
    }).length;
    const avgConversion = partners.length > 0
      ? Math.round(partners.reduce((acc, p) => acc + (partnerStatsById[p.id]?.conversion ?? 0), 0) / partners.length)
      : 0;

    return { active, attention, topPerformers, avgConversion };
  }, [partnerStatsById, partners]);

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;

    setIsDeleting(true);
    try {
      await removePartner(partnerToDelete.id);
      setPartnerToDelete(null);
      toast({
        title: "Επιτυχής διαγραφή!",
        description: "Ο συνεργάτης διαγράφηκε επιτυχώς.",
      });
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast({
        title: "Σφάλμα",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά τη διαγραφή",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const copyRegistrationLink = () => {
    const registrationURL = `${window.location.origin}/partner/register`;
    navigator.clipboard.writeText(registrationURL);
    toast({
      title: "Αντιγράφηκε!",
      description: "Ο σύνδεσμος εγγραφής αντιγράφηκε στο πρόχειρο."
    });
  };

  // Stats at a glance
  const totalLeads = leads.length;
  const totalSuccess = leads.filter(l => l.stage === "έκλεισεΗΔουλειά").length;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="Συνεργάτες"
        description="Διαχείριση δικτύου συνεργατών"
        icon={<HeartHandshake />}
        iconGradient="blue"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setShowRegLinkDialog(true)}
              className="border-slate-200 hover:bg-slate-50"
              aria-label="Εμφάνιση συνδέσμου εγγραφής"
            >
              <UserPlus className="mr-2 h-4 w-4" /> Σύνδεσμος Εγγραφής
            </Button>
            <Button asChild variant="outline" className="border-slate-200 hover:bg-slate-50">
              <Link to="/admin/partners/map">
                <Map className="mr-2 h-4 w-4" /> Χάρτης
              </Link>
            </Button>
            <Button asChild variant="gradient">
              <Link to="/admin/partners/new">
                <Plus className="mr-2 h-4 w-4" /> Νέος Συνεργάτης
              </Link>
            </Button>
          </>
        }
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          value={partners.length}
          title="Συνολικά"
          icon={<Users />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={partnersSummary.active}
          title="Ενεργοί"
          icon={<Check />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={totalLeads}
          title="Σύν. Leads"
          icon={<HeartHandshake />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={totalSuccess}
          title="Επιτυχίες"
          icon={<TrendingUp />}
          variant="outlined"
          size="compact"
        />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Αναζήτηση συνεργατών..."
                className="pl-10 bg-white border-slate-200 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <Select value={serviceFilter} onValueChange={(value) => setServiceFilter(value as ServiceType | 'all')}>
              <SelectTrigger className="w-full lg:w-52">
                <SelectValue placeholder="Υπηρεσία" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλες οι Υπηρεσίες</SelectItem>
                <SelectItem value="εξοικονομώ">Εξοικονομώ</SelectItem>
                <SelectItem value="αντλίεςΘερμότητας">Αντλίες Θερμότητας</SelectItem>
                <SelectItem value="φωτοβολταϊκά">Φωτοβολταϊκά</SelectItem>
                <SelectItem value="κλιματιστικά">Κλιματιστικά</SelectItem>
                <SelectItem value="φυσικόΑέριο">Φυσικό Αέριο</SelectItem>
                <SelectItem value="ηλιακά">Ηλιακά</SelectItem>
                <SelectItem value="άλλο">Άλλο</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'leadsDesc' | 'conversionDesc' | 'nameAsc')}>
              <SelectTrigger className="w-full lg:w-52">
                <SelectValue placeholder="Ταξινόμηση" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leadsDesc">Περισσότερα leads</SelectItem>
                <SelectItem value="conversionDesc">Υψηλότερο conversion</SelectItem>
                <SelectItem value="nameAsc">Αλφαβητικά</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={focusFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setFocusFilter('all')}
              className="h-8"
            >
              Όλοι
            </Button>
            <Button
              size="sm"
              variant={focusFilter === 'needsAttention' ? 'default' : 'outline'}
              onClick={() => setFocusFilter('needsAttention')}
              className="h-8"
            >
              <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Χρειάζονται προσοχή
              <Badge variant="secondary" className="ml-2">{partnersSummary.attention}</Badge>
            </Button>
            <Button
              size="sm"
              variant={focusFilter === 'topPerformers' ? 'default' : 'outline'}
              onClick={() => setFocusFilter('topPerformers')}
              className="h-8"
            >
              Top performers
              <Badge variant="secondary" className="ml-2">{partnersSummary.topPerformers}</Badge>
            </Button>
            {(searchTerm || serviceFilter !== 'all' || focusFilter !== 'all' || sortBy !== 'leadsDesc') && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-blue-600"
                onClick={() => {
                  setSearchTerm('');
                  setServiceFilter('all');
                  setFocusFilter('all');
                  setSortBy('leadsDesc');
                }}
              >
                Καθαρισμός
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Μέσο Conversion</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{partnersSummary.avgConversion}%</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Συνεργάτες με προσοχή</p>
            <p className="text-2xl font-semibold text-amber-600 mt-1">{partnersSummary.attention}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Top performers</p>
            <p className="text-2xl font-semibold text-emerald-600 mt-1">{partnersSummary.topPerformers}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="active" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Ενεργοί ({activePartners.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Ανενεργοί ({inactivePartners.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          {activePartners.length === 0 ? (
            <EmptyState
              icon={<Users />}
              title="Δεν βρέθηκαν ενεργοί συνεργάτες"
              description={searchTerm ? "Δοκιμάστε διαφορετικούς όρους αναζήτησης" : undefined}
              action={
                searchTerm ? (
                  <Button variant="ghost" onClick={() => setSearchTerm("")}>
                    Καθαρισμός φίλτρων
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {activePartners.map((partner, index) => (
                <Card
                  key={partner.id}
                  className="group border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                          {partner.name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{partner.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {partner.location}
                          </CardDescription>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label={`Ενέργειες για ${partner.name}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ενέργειες</DropdownMenuLabel>
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/partners/edit/${partner.id}`} className="cursor-pointer flex items-center">
                              <Edit className="mr-2 h-4 w-4" /> Επεξεργασία
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setPartnerToDelete(partner)}
                            className="text-destructive focus:bg-destructive/10"
                          >
                            <Trash className="mr-2 h-4 w-4" /> Διαγραφή
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Contact info */}
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {partner.phone}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4 text-slate-400" />
                        {partner.email || <span className="text-slate-400">-</span>}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-2">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                        {partnerStatsById[partner.id]?.leads ?? 0} Leads
                      </Badge>
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                        {partnerStatsById[partner.id]?.success ?? 0} Επιτυχίες
                      </Badge>
                      <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                        {partnerStatsById[partner.id]?.conversion ?? 0}% Conversion
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {partnerStatsById[partner.id]?.needsAttention && (
                        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
                          Χρειάζεται προσοχή
                        </Badge>
                      )}
                      {partner.sms_opt_out && (
                        <Badge variant="outline" className="text-slate-600">
                          SMS opt-out
                        </Badge>
                      )}
                    </div>

                    {/* Services */}
                    <div className="flex flex-wrap gap-1.5">
                      {partner.services && partner.services.length > 0 ? (
                        partner.services.slice(0, 3).map((service, idx) => (
                          <ServiceBadge key={idx} service={service} />
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">Χωρίς υπηρεσίες</span>
                      )}
                      {partner.services && partner.services.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{partner.services.length - 3}</Badge>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Link to={`/admin/partners/${partner.id}/dashboard`} className="w-full">
                      <Button variant="ghost" className="w-full justify-between text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                        Προβολή στατιστικών
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive" className="mt-6">
          {inactivePartners.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">Δεν υπάρχουν ανενεργοί συνεργάτες</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {inactivePartners.map((partner) => (
                <Card key={partner.id} className="border-slate-200 opacity-60 hover:opacity-100 transition-opacity">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center text-white font-bold text-sm">
                          {partner.name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{partner.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {partner.location}
                          </CardDescription>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/partners/edit/${partner.id}`}>
                              <Edit className="mr-2 h-4 w-4" /> Επεξεργασία
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setPartnerToDelete(partner)} className="text-destructive">
                            <Trash className="mr-2 h-4 w-4" /> Διαγραφή
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="outline" className="text-slate-500">Ανενεργός</Badge>
                    <div className="mt-3 flex gap-2">
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {partnerStatsById[partner.id]?.leads ?? 0} Leads
                      </Badge>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {partnerStatsById[partner.id]?.conversion ?? 0}% Conversion
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <AlertDialog
        open={!!partnerToDelete}
        onOpenChange={(open) => !open && setPartnerToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Είστε βέβαιοι;</AlertDialogTitle>
            <AlertDialogDescription>
              Η ενέργεια αυτή θα διαγράψει μόνιμα τον συνεργάτη
              {partnerToDelete?.name && ` "${partnerToDelete.name}"`}.
              <br /><br />
              <span className="text-amber-600 font-medium">
                Συνεργάτες με leads δεν μπορούν να διαγραφούν.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Άκυρο</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePartner}
              className="bg-destructive hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Registration Link Dialog */}
      <Dialog open={showRegLinkDialog} onOpenChange={setShowRegLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Σύνδεσμος Εγγραφής Συνεργατών</DialogTitle>
            <DialogDescription>
              Μοιραστείτε τον σύνδεσμο για αυτόματη εγγραφή συνεργατών.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Κωδικός εγγραφής:</strong> !Partner25
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={`${window.location.origin}/partner/register`}
                className="bg-slate-50"
              />
              <Button onClick={copyRegistrationLink} className="shrink-0">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Κλείσιμο</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Partners;
